import type {
  ChatBotSettingsFormValues,
  ChatBotUIConversationMessage,
  ChatBotUIMessage,
  ChatBotUIRef,
  ChatBotUISendMessagePayload,
} from "@jugl-web/domain-resources/chat-bot";
import { ChatBotUI } from "@jugl-web/domain-resources/chat-bot";
import {
  useAgentImageSource,
  useChatImageSource,
} from "@jugl-web/domain-resources/chat-bot/hooks/chatBotMediaSourceResolvers";
import {
  buildChatBotUIMessagePayload,
  transformChatBotUIMessagePayloadToBackendResponsePayload,
} from "@jugl-web/domain-resources/chat-bot/utils/chatBotUITransformers";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { resolveChatMessagePayloadToText } from "@jugl-web/rest-api/integrations/models/ChatBotMessage";
import { cx, getUniqueId, useToast, useTranslations } from "@jugl-web/utils";
import { createPlaygroundMessagesStoreHook } from "@web-src/modules/cx/utils/createPlaygroundMesagesStoreHook";
import { FC, useCallback, useDeferredValue, useMemo, useRef, useState } from "react";
import { BackdropContainer } from "./components/BackdropContainer";
import { ButtonPositionIndicators } from "./components/ButtonPositionIndicators";
import { CTAPreview } from "./components/CTAPreview";
import { ChatBotButton } from "./components/ChatBotButton";
import {
  BUTTON_SIZE,
  INITIAL_INCOMING_MESSAGE,
  MOCKED_OUTGOING_MESSAGE,
  PREVIEW_USER_NAME,
  WELCOME_MESSAGE_ID,
} from "./consts";

export type ChatBotChannelPreviewMode =
  | "default"
  | "launcher"
  | "ctas"
  | "suggestions"
  | "chat-only";

export interface ChatbotChannelPreviewProps extends ChatBotSettingsFormValues {
  entityId: string;
  isPreview?: boolean;
  chatMode?: "active" | "mocked";
  onRevise?: (params: { question: string; agentAnswer: string }) => void;
  previewMode?: ChatBotChannelPreviewMode;
}

const useChatBotChannelPreviewMessagesStore =
  createPlaygroundMessagesStoreHook<ChatBotUIConversationMessage>([
    INITIAL_INCOMING_MESSAGE,
  ]);

export const ChatBotChannelPreview: FC<ChatbotChannelPreviewProps> = ({
  entityId,
  chatName,
  agentName,
  welcomeMessage,
  senderMessageBackgroundColor,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  theme,
  buttonColor,
  alignment,
  bottomSpacing,
  sideSpacing,
  chatImage,
  agentImage,
  buttonIcon,
  ctas,
  ctaVisual,
  suggestions,
  chatMode = "active",
  previewMode = "default",
  isPreview,
  onRevise,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();

  const { aiWorkflowsApi } = useRestApiProvider();

  const [sendAgentChatMessage, { isLoading: isSendAgentChatMessageLoading }] =
    aiWorkflowsApi.useSendAgentChatMessageMutation();

  /**
   * Defer the form-driven props so that typing in any input field does
   * not force an immediate re-render of the potentially-large message list.
   */
  const deferredAgentName = useDeferredValue(agentName);
  const deferredAgentImage = useDeferredValue(agentImage);
  const deferredWelcomeMessage = useDeferredValue(welcomeMessage);

  const chatImageSrc = useChatImageSource(chatImage);
  const agentImageSrc = useAgentImageSource(deferredAgentImage);

  const activeChatMessagesSource =
    useChatBotChannelPreviewMessagesStore(entityId);

  const mockedChatMessagesSource = useMemo(() => {
    return {
      messages: [INITIAL_INCOMING_MESSAGE, MOCKED_OUTGOING_MESSAGE],
      setMessages: () => {},
    };
  }, []);

  const { messages, setMessages } =
    chatMode === "active" ? activeChatMessagesSource : mockedChatMessagesSource;

  const chatBotUIRef = useRef<ChatBotUIRef>(null);

  const [isResetting, setIsResetting] = useState(false);

  /** Reset to a fresh welcome message; display values are re-applied by displayMessages. */
  const resetMessages = useCallback(async () => {
    if (chatMode === "mocked") {
      return;
    }

    try {
      setIsResetting(true);

      await sendAgentChatMessage({
        entityId,
        // Agent will reset the context once we type "reset" in the chat
        input: "reset",
        medium: "chatbot",
        agent_id: entityId,
        preview: isPreview || undefined,
      }).unwrap();

      setMessages([INITIAL_INCOMING_MESSAGE]);
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    } finally {
      setIsResetting(false);
    }
  }, [
    chatMode,
    entityId,
    isPreview,
    sendAgentChatMessage,
    setMessages,
    t,
    toast,
  ]);

  const filteredSuggestions = useMemo(
    () => suggestions.map((s) => s.value.trim()).filter((v) => v.length > 0),
    [suggestions]
  );

  const hasConversationJustStarted = useMemo(
    () =>
      messages.length === 0 ||
      (messages.length === 1 && messages[0].id === WELCOME_MESSAGE_ID),
    [messages]
  );

  const handleSendMessage = useCallback(
    async (payload: ChatBotUISendMessagePayload) => {
      if (chatMode === "mocked") {
        return;
      }

      const backendResponsePayload =
        transformChatBotUIMessagePayloadToBackendResponsePayload(payload);

      const agentInput = backendResponsePayload.text;
      const plainText = resolveChatMessagePayloadToText(backendResponsePayload);

      const optimisticId = getUniqueId();

      const optimisticMessage: ChatBotUIMessage = {
        id: optimisticId,
        type: "outgoing",
        timestamp: new Date(),
        payload: {
          type: "text",
          text: plainText,
          quickReplies: [],
        },
        senderName: PREVIEW_USER_NAME,
        senderImageUrl: "",
      };

      setMessages((prev) => [...prev, optimisticMessage]);

      window.setTimeout(() => {
        chatBotUIRef.current?.scrollToLastMessage();
      }, 100);
      try {
        const resp = await sendAgentChatMessage({
          entityId,
          input: agentInput,
          medium: "chatbot",
          agent_id: entityId,
          preview: isPreview || undefined,
        }).unwrap();

        const agentMessage: ChatBotUIMessage = {
          id: getUniqueId(),
          type: "incoming",
          timestamp: new Date(),
          payload: buildChatBotUIMessagePayload({
            plainText: resp.agent_response,
            quickReplies: resp.quick_replies ?? [],
            ctaToolResult: resp.cta_tool_result,
          }),
          senderName: agentName || "Support",
          senderImageUrl: agentImageSrc,
        };

        setMessages((prev) => [...prev, agentMessage]);

        window.setTimeout(() => {
          chatBotUIRef.current?.scrollToLastMessage();
        }, 100);
      } catch {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));

        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
      }
    },
    [
      chatMode,
      setMessages,
      sendAgentChatMessage,
      entityId,
      isPreview,
      agentName,
      agentImageSrc,
      toast,
      t,
    ]
  );

  const displayMessages = useMemo(
    () =>
      messages.map<ChatBotUIConversationMessage>((message) => {
        if (message.type === "outgoing") {
          return message;
        }

        return {
          ...message,
          senderName: deferredAgentName,
          senderImageUrl: agentImageSrc,
          ...(message.id === "welcome"
            ? {
                payload: {
                  ...message.payload,
                  text: deferredWelcomeMessage,
                },
              }
            : {}),
        };
      }),
    [messages, deferredAgentName, agentImageSrc, deferredWelcomeMessage]
  );

  return (
    <div className="flex h-full w-full justify-center py-6">
      {/* Inner container */}
      <BackdropContainer
        alignment={alignment}
        isBackdropVisible={previewMode === "launcher" || previewMode === "ctas"}
        className={cx(
          "my-auto",
          previewMode === "ctas" && "h-auto min-h-[300px] justify-end"
        )}
      >
        {/* Chatbot Frame */}
        {previewMode !== "ctas" && (
          <div className="h-full w-full max-w-[410px] overflow-hidden rounded-3xl shadow-[0_8px_24px_rgba(0,0,0,0.15)]">
            <ChatBotUI
              ref={chatBotUIRef}
              messages={displayMessages}
              suggestions={
                hasConversationJustStarted || previewMode === "suggestions"
                  ? filteredSuggestions
                  : undefined
              }
              config={{
                headerTitle: chatName,
                userBubbleColor: senderMessageBackgroundColor,
                agentName: deferredAgentName,
                agentImageUrl: agentImageSrc,
                chatImageUrl: chatImageSrc,
              }}
              user={{ name: PREVIEW_USER_NAME }}
              isInitialLoading={isResetting}
              isTyping={isSendAgentChatMessageLoading}
              isInputDisabled={chatMode === "mocked"}
              isSending={isSendAgentChatMessageLoading}
              onSendMessage={handleSendMessage}
              onRestart={chatMode === "active" ? resetMessages : undefined}
              getMessageActions={(message, index, allMessages) => {
                if (message.type !== "incoming" || !onRevise) return undefined;

                const prev = allMessages[index - 1];

                if (prev?.type === "outgoing" && prev.payload.type === "text") {
                  const question = prev.payload.text;
                  const agentAnswer = message.payload.text;

                  return {
                    onRevise: () =>
                      onRevise({
                        question,
                        agentAnswer,
                      }),
                  };
                }

                return undefined;
              }}
            />
          </div>
        )}
        {previewMode === "ctas" && (
          <CTAPreview
            ctas={ctas}
            ctaVisual={ctaVisual}
            alignment={alignment}
            buttonSize={BUTTON_SIZE}
          />
        )}
        {/* Launcher button */}
        {previewMode !== "chat-only" && (
          <ChatBotButton
            isOpen={false}
            icon={buttonIcon}
            buttonColor={buttonColor}
            className={cx(
              "z-10",
              alignment === "right" ? "self-end" : "self-start"
            )}
          />
        )}
        {/* Vectors */}
        {previewMode === "launcher" && (
          <ButtonPositionIndicators
            alignment={alignment}
            bottomSpacing={bottomSpacing}
            sideSpacing={sideSpacing}
          />
        )}
      </BackdropContainer>
    </div>
  );
};
